import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { PageHeader } from "@/components/ui/PageHeader";
import {
  listCategories,
  createEvent,
  publishEvent,
  previewPublish,
  unpublishEvent,
  cancelScheduledPublish,
  cancelEvent,
  myOrganizedEvents,
  checkInTicket,
  checkInStats,
  type CreateEventInput,
  type OrganizedEvent,
} from "@/api/events";
import { listTiers, createTier } from "@/api/tickets";
import { EventGraphPanel } from "./EventGraphPanel";
import { EventPreparationPanel } from "./EventPreparationPanel";
import { EventJourneyPanel } from "./EventJourneyPanel";
import { EventMediaPanel } from "./EventMediaPanel";
import { EventEditionsPanel } from "./EventEditionsPanel";
import { AdmitsPanel } from "./AdmitsPanel";
import { SevaPanel } from "./SevaPanel";
import { HostToolsPanel } from "./HostToolsPanel";
import { EventAboutPanel } from "./EventAboutPanel";
import { HostAudiencePanel } from "./HostAudiencePanel";
import { QuestionCatalogPanel } from "./QuestionCatalogPanel";
import { HostingSeasonPanel } from "./HostingSeasonPanel";

/**
 * Organizer console — the supply side of the events pillar. Everything an
 * organizer does after registering: create → add tiers → publish →
 * check people in. One page so the whole lifecycle is testable end-to-end.
 */
export default function OrganizerLab() {
  const qc = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: mine } = useQuery({
    queryKey: ["organized-events"],
    queryFn: myOrganizedEvents,
  });
  const events = mine?.data?.events ?? [];
  const selected = events.find((e) => e.id === selectedId) ?? null;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Organizer Console"
        subtitle="Create an event, add ticket tiers, publish it, and check attendees in"
      />

      <HostAudiencePanel />
      <HostingSeasonPanel />
      <QuestionCatalogPanel />
      <CreateEventForm onCreated={(id) => setSelectedId(id)} />

      {/* My events */}
      <div className="card p-5">
        <h2 className="font-semibold mb-3">My events ({events.length})</h2>
        {events.length === 0 && (
          <p className="text-sm text-gray-500">Nothing yet — create one above.</p>
        )}
        <div className="space-y-2">
          {events.map((e) => (
            <button
              key={e.id}
              onClick={() => setSelectedId(e.id === selectedId ? null : e.id)}
              className={`w-full text-left p-3 rounded-lg border transition-colors ${
                e.id === selectedId ? "border-primary-400 bg-primary-50" : "border-gray-200 hover:bg-gray-50"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-medium">{e.title}</span>
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                  {e.eventStatus}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">
                {new Date(e.eventDate).toLocaleDateString()} · {e.city}, {e.state}
                {e.questionsWaiting ? ` · ${e.questionsWaiting} waiting for your answer` : ""}
              </p>
            </button>
          ))}
        </div>
      </div>

      {selected && <ManagePanel event={selected} onChanged={() => qc.invalidateQueries({ queryKey: ["organized-events"] })} />}
    </div>
  );
}

function CreateEventForm({ onCreated }: { onCreated: (id: string) => void }) {
  const qc = useQueryClient();
  const { data: cats } = useQuery({ queryKey: ["event-categories"], queryFn: listCategories });
  const categories = cats?.data?.categories ?? [];

  const [form, setForm] = useState<CreateEventInput>({
    title: "",
    categoryCode: "",
    eventDate: "",
    startTime: "18:00",
    city: "",
    state: "",
    venueName: "",
  });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => createEvent(form),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["organized-events"] });
      setError(null);
      if (res.data?.id) onCreated(res.data.id);
      setForm({ title: "", categoryCode: "", eventDate: "", startTime: "18:00", city: "", state: "", venueName: "" });
    },
    onError: (e: Error) => setError(e.message),
  });

  const set = (k: keyof CreateEventInput) => (ev: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setForm((f) => ({ ...f, [k]: ev.target.value }));

  const valid = form.title.length >= 5 && form.categoryCode && form.eventDate && form.city && form.state;

  return (
    <div className="card p-5">
      <h2 className="font-semibold mb-3">Create event</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input className="input sm:col-span-2" placeholder="Title (min 5 chars)" value={form.title} onChange={set("title")} />
        <select className="input" value={form.categoryCode} onChange={set("categoryCode")}>
          <option value="">Category…</option>
          {categories.map((c) => (
            <option key={c.code} value={c.code}>{c.name}</option>
          ))}
        </select>
        <input className="input" type="date" value={form.eventDate} onChange={set("eventDate")} />
        <input className="input" type="time" value={form.startTime} onChange={set("startTime")} />
        <input className="input" placeholder="Venue (optional)" value={form.venueName} onChange={set("venueName")} />
        <input className="input" placeholder="City" value={form.city} onChange={set("city")} />
        <input className="input" placeholder="State" value={form.state} onChange={set("state")} />
      </div>
      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
      <button
        className="btn-primary mt-3"
        disabled={!valid || create.isPending}
        onClick={() => create.mutate()}
      >
        {create.isPending ? "Creating…" : "Create draft"}
      </button>
    </div>
  );
}

/** What Publish would do, read before the tap: blockers, a free tier, who is told. */
function PublishPreviewNote({ eventId }: { eventId: string }) {
  const { data, error } = useQuery({
    queryKey: ["publish-preview", eventId],
    queryFn: () => previewPublish(eventId),
  });
  if (error) return <p className="text-xs text-red-600">Preview failed: {(error as Error).message}</p>;
  const p = data?.data;
  if (!p) return null;
  return (
    <div className="text-xs text-gray-600 space-y-1">
      {p.blockers.map((b) => (
        <p key={b.code} className="text-red-700">Cannot publish yet: {b.message} ({b.code})</p>
      ))}
      {p.willCreateFreeTier && <p>Publishing adds a free ticket kind, because there is none.</p>}
      {p.scheduledPublishAt && <p>Goes live at {new Date(p.scheduledPublishAt).toLocaleString()}.</p>}
      <p>
        {p.notifies.audience === "nobody"
          ? "Nobody is told when this publishes: the host has no followers."
          : `${p.notifies.count} followers will be told.`}
      </p>
    </div>
  );
}

function ManagePanel({ event, onChanged }: { event: OrganizedEvent; onChanged: () => void }) {
  const qc = useQueryClient();
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [publishAt, setPublishAt] = useState("");

  const { data: tiersRes } = useQuery({
    queryKey: ["tiers", event.id],
    queryFn: () => listTiers(event.id),
  });
  const tiers = tiersRes?.data.tiers ?? [];

  const { data: statsRes } = useQuery({
    queryKey: ["check-in-stats", event.id],
    queryFn: () => checkInStats(event.id),
    enabled: event.eventStatus === "published" || event.eventStatus === "sold_out",
  });

  const act = (fn: () => Promise<unknown>, label: string) =>
    fn()
      .then(() => {
        setActionMsg(`${label} ✓`);
        onChanged();
      })
      .catch((e: Error) => setActionMsg(`${label} failed: ${e.message}`));

  // Tier form
  const [tierName, setTierName] = useState("General");
  const [tierPrice, setTierPrice] = useState(0);
  const [tierQty, setTierQty] = useState(50);
  const addTier = useMutation({
    mutationFn: () =>
      createTier(event.id, { name: tierName, priceCents: tierPrice, quantityTotal: tierQty }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["tiers", event.id] }),
    onError: (e: Error) => setActionMsg(`Tier failed: ${e.message}`),
  });

  // Check-in
  const [code, setCode] = useState("");
  const checkIn = useMutation({
    mutationFn: () => checkInTicket(event.id, code.trim()),
    onSuccess: () => {
      setActionMsg("Checked in ✓");
      setCode("");
      qc.invalidateQueries({ queryKey: ["check-in-stats", event.id] });
    },
    onError: (e: Error) => setActionMsg(`Check-in failed: ${e.message}`),
  });

  return (
    <div className="card p-5 space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="font-semibold">
          Manage: {event.title}{" "}
          <span className="text-xs font-normal text-gray-500">({event.eventStatus})</span>
        </h2>
        <Link to={`/lab/events/${event.id}/thread`} className="text-sm text-primary-600 hover:underline">
          thread →
        </Link>
      </div>

      {/* Lifecycle */}
      {event.eventStatus === "draft" && <PublishPreviewNote eventId={event.id} />}
      <div className="flex gap-2 flex-wrap">
        <button
          className="btn-primary"
          disabled={event.eventStatus !== "draft"}
          onClick={() => act(() => publishEvent(event.id), "Published")}
        >
          Publish
        </button>
        {event.eventStatus === "draft" && (
          <>
            <input
              type="datetime-local"
              className="input w-52"
              aria-label="Publish at"
              value={publishAt}
              onChange={(e) => setPublishAt(e.target.value)}
            />
            <button
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium"
              disabled={!publishAt}
              onClick={() => act(() => publishEvent(event.id, new Date(publishAt).toISOString()), "Scheduled")}
            >
              Publish at this time
            </button>
            <button
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium"
              onClick={() => act(() => cancelScheduledPublish(event.id), "Schedule cleared")}
            >
              Clear schedule
            </button>
          </>
        )}
        {event.eventStatus === "published" && (
          <button
            className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium"
            onClick={() => act(() => unpublishEvent(event.id), "Back to draft")}
          >
            Back to draft
          </button>
        )}
        <button
          className="px-4 py-2 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 text-sm font-medium"
          disabled={event.eventStatus === "cancelled"}
          onClick={() => act(() => cancelEvent(event.id, "Cancelled from Lab"), "Cancelled")}
        >
          Cancel event
        </button>
      </div>

      <EventAboutPanel key={'about:' + event.id} eventId={event.id} />
      <EventGraphPanel key={event.id} eventId={event.id} />
      <EventJourneyPanel key={'journey:' + event.id} eventId={event.id} />
      <EventPreparationPanel key={'prepare:' + event.id} eventId={event.id} />
      <EventMediaPanel key={'media:' + event.id} eventId={event.id} />
      <EventEditionsPanel key={'editions:' + event.id} eventId={event.id} />
      <AdmitsPanel key={'admits:' + event.id} eventId={event.id} />
      <SevaPanel key={'seva:' + event.id} eventId={event.id} />
      <HostToolsPanel key={'host:' + event.id} eventId={event.id} />

      {/* Tiers */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-2">Ticket tiers ({tiers.length})</h3>
        {tiers.map((t) => (
          <p key={t.id} className="text-sm text-gray-600">
            {t.name} — {t.priceCents === 0 ? "Free" : `$${(t.priceCents / 100).toFixed(2)}`} ·{" "}
            {t.quantityAvailable === -1 ? "unlimited" : `${t.quantityAvailable} available`}
          </p>
        ))}
        <div className="flex gap-2 mt-2 flex-wrap">
          <input className="input w-32" placeholder="Name" value={tierName} onChange={(e) => setTierName(e.target.value)} />
          <input className="input w-28" type="number" min={0} placeholder="Cents" value={tierPrice} onChange={(e) => setTierPrice(Number(e.target.value))} />
          <input className="input w-24" type="number" min={-1} placeholder="Qty" value={tierQty} onChange={(e) => setTierQty(Number(e.target.value))} />
          <button className="btn-primary" disabled={addTier.isPending || !tierName} onClick={() => addTier.mutate()}>
            Add tier
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-1">Price in cents (0 = free) · quantity −1 = unlimited</p>
      </div>

      {/* Check-in */}
      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-2">Check-in</h3>
        <div className="flex gap-2">
          <input
            className="input flex-1"
            placeholder="Ticket code (WD-TKT-…)"
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
          <button className="btn-primary" disabled={!code.trim() || checkIn.isPending} onClick={() => checkIn.mutate()}>
            Check in
          </button>
        </div>
        {statsRes?.data && (
          <pre className="text-xs bg-gray-50 rounded p-2 mt-2 overflow-x-auto">
            {JSON.stringify(statsRes.data, null, 2)}
          </pre>
        )}
      </div>

      {actionMsg && <p className="text-sm text-gray-600">{actionMsg}</p>}
    </div>
  );
}
